import React, { Fragment, FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Row as BSRow,
    Table,
    DropdownButton,
    Dropdown,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { AuthedLayout } from '../../../components';
import { useSemGrade } from '../../../hook';
import { CleanTeethStudentStatistic, UserInfo } from '../../../model';
import apis from '../../../apis';
import {
    Cell,
    Row,
    downloadSheets,
    generateSheet,
    listToObject,
} from '../../../utils';
import XLSX from 'xlsx-js-style';
import { toast } from 'react-toastify';


const borderStyle = { color: { rgb: '000000' }, style: 'thin' } as const;
const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;
const bottomBorderStyle = {
    bottom: borderStyle,
};
const topBottomBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
};
const cellAlignStyle = {
    horizontal: 'left',
    vertical: 'center',
    wrapText: true,
} as const;


const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const teeCleanStaticClass: FunctionComponent<Props> = ({ user, catchErrorForModal }) => {
    const { yearSem, element } = useSemGrade();
    const [datas, setCleanTeethStudent] = useState<CleanTeethStudentStatistic[]>(
        []
    );
    const [userinfo, setUser] = useState<UserInfo>();
    useEffect(() => {
        if (yearSem) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);

            toast
                .promise(
                    apis
                        .getTeethTeeClassCleanStatic(yearSem.year, yearSem.sem)
                        .then(r => {
                            setCleanTeethStudent(r);
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
        }
    }, [yearSem?.year, yearSem?.sem]);

    const gradeBase = datas.reduce((grades, d) => {
        if (!grades[d.grade]) {
            grades[d.grade] = {};
        }

        if (!grades[d.grade][d.classNo ?? 0]) {
            grades[d.grade][d.classNo ?? 0] = [];
        }
        grades[d.grade][d.classNo ?? 0].push(d);
        return grades;
    }, {} as Record<number, Record<number, CleanTeethStudentStatistic[]>>);

    // const { schoolExpected, schoolDont } = useMemo<{
    //     schoolExpected: number;
    //     schoolDont: number;
    // }>(() => {
    //     let expected = 0;
    //     let dont = 0;

    //     datas.forEach(d => {
    //         expected += d.studentCount * d.brushTimes;
    //         dont += d.stDontBrushTimes;
    //     });

    //     return {
    //         schoolExpected: expected,
    //         schoolDont: dont,
    //     };
    // }, [datas]);

    const {
        schoolStudentCount,
        schoolBrushTimes,
        schoolExpected,
        schoolDont,
    } = useMemo(() => {
        const classGroups = datas.reduce(
            (result, item) => {
                const key = `${item.grade}-${item.classNo}`;

                if (!result[key]) {
                    result[key] = [];
                }

                result[key].push(item);
                return result;
            },
            {} as Record<string, CleanTeethStudentStatistic[]>
        );

        let studentCount = 0;
        let brushTimes = 0;
        let expected = 0;
        let dont = 0;

        Object.values(classGroups).forEach((classdata) => {
            studentCount += classdata.reduce(
                (max, item) =>
                    Math.max(max, item.studentCount ?? 0),
                0
            );

            brushTimes += classdata.reduce(
                (sum, item) =>
                    sum + (item.brushTimes ?? 0),
                0
            );
        });

        datas.forEach((item) => {
            expected +=
                (item.studentCount ?? 0) *
                (item.brushTimes ?? 0);

            dont += item.stDontBrushTimes ?? 0;
        });

        return {
            schoolStudentCount: studentCount,
            schoolBrushTimes: brushTimes,
            schoolExpected: expected,
            schoolDont: dont,
        };
    }, [datas]);


    // // 組成table cell
    const contentRows: Row[] = Object.values(gradeBase).flatMap(
        (gs, idx, allArray) => {
            // 同一年級下 loop每個班級
            const rows = teethToRow(Object.keys(gradeBase)[idx], gs);
            return rows;
        }
    );

    const content: Row[] = [
        {
            cells: [
                {
                    value: '年級',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '班級',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '月份',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '學生數',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '實施日數',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '未實施人次',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '執行率',
                    style: { border: fullBorderStyle },
                }
            ],
        },
        ...contentRows,
        {
            cells: [
                {
                    value: '總計',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '',
                    style: { border: fullBorderStyle },
                },
                {
                    value: '',
                    style: { border: fullBorderStyle },
                },
                {
                    value: schoolStudentCount,
                    style: { border: fullBorderStyle },
                },
                {
                    value: schoolBrushTimes,
                    style: { border: fullBorderStyle },
                },
                {
                    value: schoolDont,
                    style: { border: fullBorderStyle },
                },
                {
                    value: percentDone(
                        schoolDont,
                        schoolExpected
                    ),
                    style: { border: fullBorderStyle },
                },
            ],
        }
    ];

    const handlePrint = () => {
        const printWindow = window.open(
            '',
            '_blank',
            'width=1200,height=900'
        );

        if (!printWindow) {
            alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
            return;
        }

        const escapeHtml = (value: unknown): string => {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        };

        const title =
            `${user.schools[user.currentSchool].name}${yearSem?.year}學年第${yearSem?.sem}潔牙執行成果統計(班級)`;

        const tableRows = content
            .map((row) => {
                const cells = row.cells
                    .map((cell) => {
                        if (cell.value === undefined) {
                            return '';
                        }

                        const colSpan = cell.merge?.column
                            ? ` colspan="${cell.merge.column}"`
                            : '';

                        const rowSpan = cell.merge?.row
                            ? ` rowspan="${cell.merge.row}"`
                            : '';

                        return `
                        <td${colSpan}${rowSpan}>
                            ${escapeHtml(cell.value)}
                        </td>
                    `;
                    })
                    .join('');

                return `<tr>${cells}</tr>`;
            })
            .join('');

        printWindow.document.open();

        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="zh-Hant">
        <head>
            <meta charset="UTF-8" />
            <title>${escapeHtml(title)}</title>

            <style>
                * {
                    box-sizing: border-box;
                }

                html,
                body {
                    margin: 0;
                    padding: 0;
                    font-family:
                        "Microsoft JhengHei",
                        "Noto Sans TC",
                        Arial,
                        sans-serif;
                    color: #000;
                }

                .print-page {
                    min-height: 277mm;
                    display: flex;
                    flex-direction: column;
                }

                h1 {
                    margin: 0 0 15px;
                    text-align: center;
                    font-size: 18px;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }

                td {
                    border: 1px solid #000;
                    padding: 5px 3px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 11px;
                    word-break: break-word;
                }

                .footer {
    margin-top: auto;
    padding-top: 16px;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    width: 100%;
}

                @page {
                    size: A4 portrait;
                    margin: 10mm;
                }

                @media print {
                    body {
                        padding: 0;
                    }

                    tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                }
            </style>
        </head>

        <body>
            <div class="print-page">

                <h1>${escapeHtml(title)}</h1>

                <table>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>

                <div class="footer">
  <span>承辦人:</span>
  <span>組長:</span>
  <span>主任:</span>
  <span>校長:</span>
</div>
            </div>

            <script>
                window.onload = function () {
                    window.focus();

                    setTimeout(function () {
                        window.print();
                    }, 300);
                };

                window.onafterprint = function () {
                    window.close();
                };
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
    };


    return (
        <AuthedLayout>
            <BSRow className="justify-content-between">
                <span className="mr-3">{element}</span>
                <DropdownButton
                    id="tee-clean-static-class-export"
                    title="匯出"
                    variant="success"
                    disabled={!datas.length}
                >
                    <Dropdown.Item
                        onClick={() => {
                            const workSheet = generateSheet(
                                [
                                    {
                                        cells: [
                                            {
                                                value: `${user.schools[user.currentSchool].name}${yearSem?.year}學年第${yearSem?.sem}潔牙執行成果統計(班級)`,
                                                style: {
                                                    alignment: {
                                                        horizontal: 'left'
                                                    }
                                                },
                                            },
                                        ],
                                    },
                                    ...content,
                                    {
                                        cells: [
                                            {
                                                value:
                                                    '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                                style: {
                                                    alignment: {
                                                        horizontal: 'left'
                                                    }
                                                },
                                            },
                                        ],
                                    },
                                ],
                                {
                                    alignment: cellAlignStyle,
                                }
                            );

                            downloadSheets(
                                `${user.schools[user.currentSchool].name}${yearSem?.year}學年第${yearSem?.sem}潔牙執行成果統計(班級)`,
                                userinfo?.exportFileType,
                                {
                                    sheet: workSheet,
                                    name: 'st'
                                }
                            );
                        }}
                    >
                        Excel下載
                    </Dropdown.Item>

                    <Dropdown.Item onClick={handlePrint}>
                        列印
                    </Dropdown.Item>
                </DropdownButton>
            </BSRow>
            <hr />
            <BSRow>
                <Table
                    striped
                    bordered
                    className="text-center"
                    style={{ tableLayout: 'fixed' }}
                >
                    <colgroup>
                        <col style={{ width: '3%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                    </colgroup>
                    <tbody>
                        {content.map((r, i) => {
                            const cells = r.cells.map((c, j) =>
                                c.value != undefined ? (
                                    <td
                                        colSpan={c.merge?.column}
                                        rowSpan={c.merge?.row}
                                        key={`${i}-${j}`}
                                    >
                                        {c.value}
                                    </td>
                                ) : (
                                    <Fragment key={`${i}-${j}`}></Fragment>
                                )
                            );
                            return <tr key={i}>{cells}</tr>;
                        })}
                    </tbody>
                </Table>
            </BSRow>
        </AuthedLayout>
    );
};


function teethToRow(
    grade: string,
    gradedata: Record<number, CleanTeethStudentStatistic[]>,
): Row[] {
    let gradeStudentCount = 0;
    let gradeBrushTimes = 0;
    let gradeDontBrush = 0;
    let gradeExpected = 0;

    const classRows = Object.entries(gradedata).flatMap(
        ([classNo, classdata]) => {
            const sortedClassData = [...classdata].sort(
                (a, b) => Number(a.month) - Number(b.month)
            );

            const classStudentCount = sortedClassData.reduce(
                (sum, item) =>
                    sum + Number(item.studentCount ?? 0),
                0
            );

            const classBrushTimes = sortedClassData.reduce(
                (sum, item) =>
                    sum + Number(item.brushTimes ?? 0),
                0
            );

            const classDontBrush = sortedClassData.reduce(
                (sum, item) =>
                    sum + Number(item.stDontBrushTimes ?? 0),
                0
            );

            const classExpected = sortedClassData.reduce(
                (sum, item) =>
                    sum +
                    Number(item.studentCount ?? 0) *
                    Number(item.brushTimes ?? 0),
                0
            );

            gradeStudentCount += classStudentCount;
            gradeBrushTimes += classBrushTimes;
            gradeDontBrush += classDontBrush;
            gradeExpected += classExpected;

            const classTotalRow: Row = {
                cells: [
                    {
                        value: '',
                        style: { border: fullBorderStyle },
                    },
                    {
                        value: classNo,
                        style: { border: fullBorderStyle },
                    },
                    {
                        value: '',
                        style: { border: fullBorderStyle },
                    },
                    {
                        value: classStudentCount,
                        style: { border: fullBorderStyle },
                    },
                    {
                        value: classBrushTimes,
                        style: { border: fullBorderStyle },
                    },
                    {
                        value: classDontBrush,
                        style: { border: fullBorderStyle },
                    },
                    {
                        value: percent(
                            classDontBrush,
                            classExpected
                        ),
                        style: { border: fullBorderStyle },
                    },
                ],
            };

            const monthRows: Row[] = sortedClassData.map(
                item => {
                    const studentCount =
                        Number(item.studentCount ?? 0);

                    const brushTimes =
                        Number(item.brushTimes ?? 0);

                    const dontBrushTimes =
                        Number(item.stDontBrushTimes ?? 0);

                    const expected =
                        studentCount * brushTimes;

                    return {
                        cells: [
                            {
                                value: '',
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                            {
                                value: '',
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                            {
                                value: String(
                                    item.month ?? ''
                                ).padStart(2, '0'),
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                            {
                                value: studentCount,
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                            {
                                value: brushTimes,
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                            {
                                value: dontBrushTimes,
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                            {
                                value: percent(
                                    dontBrushTimes,
                                    expected
                                ),
                                style: {
                                    border: fullBorderStyle,
                                },
                            },
                        ],
                    };
                }
            );

            return [
                classTotalRow,
                ...monthRows,
            ];
        }
    );

    const gradeTotalRow: Row = {
        cells: [
            {
                value: grade,
                style: { border: fullBorderStyle },
            },
            {
                value: '',
                style: { border: fullBorderStyle },
            },
            {
                value: '',
                style: { border: fullBorderStyle },
            },
            {
                value: '',
                style: { border: fullBorderStyle },
            },
            {
                value: gradeBrushTimes,
                style: { border: fullBorderStyle },
            },
            {
                value: gradeDontBrush,
                style: { border: fullBorderStyle },
            },
            {
                value: percent(
                    gradeDontBrush,
                    gradeExpected
                ),
                style: { border: fullBorderStyle },
            },
        ],
    };

    return [
        gradeTotalRow,
        ...classRows,
    ];
}

function percent(dont: number, expected: number): string {
    if (!expected) return '0.00%';

    const done = expected - dont;
    return ((done / expected) * 100).toFixed(2) + '%';
}

function percentDone(dont: number, expected: number): string {
    if (!expected) return "0.00%";
    return (((expected - dont) / expected) * 100).toFixed(2) + "%";
}
export const TeeCleanStaticClass = connector(teeCleanStaticClass);
